import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { A, HyperLink } from 'styles/Styles.styled';
import { Theme } from 'styles/theme';
import { FieldPanelHeader } from 'styles/v2/Styles.styled';
import { HelpBox } from 'components/v2/help-boxes';
import { DocumentPropertyFieldsAreaId } from 'types/dynamic-forms/DocumentPropertyFieldsAreaId';
import { useDocumentStore } from '../hooks/useDocumentStore';

const PublicationConfirmationRenderer: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const { state } = useDocumentStore();

  // The confirmation-for-publication block is an owner action: show it only while the confirmation
  // fields-area is editable (backend gates this on role + verification type + state, e.g.
  // EPD Verification only in "Ready to publish", EPD Process Certification while drafting).
  if (!state.fields[DocumentPropertyFieldsAreaId.Confirmation]) {
    return null;
  }

  return (
    <>
      <FieldPanelHeader>Confirmation for publication</FieldPanelHeader>
      <HelpBox>
        <span>EPD Owner should accept the service agreement and submit the EPD for publication. </span>
      </HelpBox>
      <ContentWrapper>
        <p>
          As an authorized person of the EPD Owner, the EPD Owner hereby requests to register and publish the EPD in the
          International EPD System, and confirms the accuracy and completeness of the attached documentation and its
          contents, and hereby undertake to observe the terms and conditions stated in the Service Agreement including its
          appendix, and the from time to time applicable General Programme Instructions, and the applicable PCR for the EPD
          included in this Agreement.
        </p>
        <p>
          The EPD Owner agrees that name and e-mail address provided above and in the submitted EPD may be stored
          electronically, may be used as contact information regarding the EPD and the services under this Agreement, and
          that the data displayed in the EPD will be published at{' '}
          <HyperLink target="_blank" rel="noopener noreferrer" href="https://www.environdec.com">
            www.environdec.com
          </HyperLink>
          .
          <br />
          EPDs using the ECO Platform logo and machine-readable EPD files will also be published on other relevant
          platforms.
        </p>
        <p>
          <strong>{t('epdWizard.wizardStep4.properties.confirmation.infoFees')}</strong>
        </p>
        <ServiceAgreementArea href={theme.serviceAgreementUrl}>
          <i className="pi pi-external-link" style={{ fontSize: '0.75rem', color: 'White', marginRight: '0.5rem' }}></i>
          <p style={{ color: 'White', margin: 0 }}>Service agreement</p>
        </ServiceAgreementArea>
      </ContentWrapper>
    </>
  );
};

const ContentWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  padding: 1rem 1.5rem 1rem 1rem;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;

  p {
    margin-top: 0;
  }
`;

const ServiceAgreementArea = styled(A)`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  width: 160px;
  height: 2.5vh;
  background-color: ${(props) => props.theme.colors.primaryOrange};
  border-radius: 2px;
  &:hover {
    background-color: ${(props) => props.theme.colors.lightOrange};
  }
`;

export default PublicationConfirmationRenderer;
